import type {
  ApiReconstructedDocumentDetail,
  ProjectPageProcessing,
  ProjectPageProcessingCounts,
  ProjectPageProcessingStage,
} from '@avisoma/shared';

const PAGE_STAGES: Exclude<ProjectPageProcessingStage, 'pages'>[] = [
  'pdfParser',
  'metadata',
  'embeddings',
  'classification',
  'dataExtraction',
];

const empty = (): ProjectPageProcessingCounts => ({
  done: 0,
  processing: 0,
  error: 0,
  skip: 0,
  pending: 0,
});

/** Reduce the per-page processing entries on a ReconstructedDocumentDetail into
 * the same `ProjectPageProcessing` shape that ProjectView feeds into
 * `<PageProcessingSummary>`. The `pages` stage stays at all zeros — a
 * reconstructed entity has no document-splitting step of its own, so the summary
 * dims that row instead of reporting a misleading count. */
export function aggregateConstituentPageProcessing(
  entity: Pick<ApiReconstructedDocumentDetail, 'pages' | 'sourceDocuments'>,
): ProjectPageProcessing {
  const stages: ProjectPageProcessing['stages'] = {
    pages: empty(),
    pdfParser: empty(),
    metadata: empty(),
    embeddings: empty(),
    classification: empty(),
    dataExtraction: empty(),
  };

  for (const p of entity.pages) {
    for (const stage of PAGE_STAGES) {
      const status = p.processing[stage]?.status;
      bumpStage(stages[stage], status);
    }
  }

  return {
    totalPages: entity.pages.length,
    totalDocuments: entity.sourceDocuments.length,
    stages,
  };
}

/** True if any constituent page still has a stage that hasn't reached a
 * terminal state (`done` / `error` / `skip`). Drives the
 * ReconstructedDocumentView auto-refresh: while anything is still pending or
 * processing we poll, and once everything settles we stop. `undefined` status
 * means the stage hasn't been recorded yet (fresh page) and is treated the
 * same as `pending`. */
export function hasUnfinishedPageWork(
  entity: Pick<ApiReconstructedDocumentDetail, 'pages'>,
): boolean {
  return entity.pages.some((p) =>
    PAGE_STAGES.some((stage) => {
      const status = p.processing[stage]?.status;
      return status === 'pending' || status === 'processing' || status === undefined;
    }),
  );
}

function bumpStage(
  counts: ProjectPageProcessingCounts,
  status: 'done' | 'processing' | 'error' | 'skip' | 'pending' | undefined,
): void {
  switch (status) {
    case 'done':
      counts.done += 1;
      return;
    case 'processing':
      counts.processing += 1;
      return;
    case 'error':
      counts.error += 1;
      return;
    case 'skip':
      counts.skip += 1;
      return;
    // 'pending' and undefined both fall through to the pending bucket — the
    // page exists but the stage hasn't started, which is the same thing as far
    // as the summary's denominator math is concerned.
    default:
      counts.pending += 1;
      return;
  }
}
